import { Box, FormControl, FormControlLabel, FormHelperText, Switch as MuiSwitch, SwitchProps } from '@mui/material';
import React from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import slugify from 'utils/slugify';

type CommonProps = SwitchProps & {
  label: string;
  margin?: 'none' | 'dense' | 'normal';
  error?: boolean;
  helperText?: string;
  dark?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
};

type SwitchExtProps =
  | { value?: boolean; controller: UseControllerProps<any> }
  | { value: boolean; controller?: UseControllerProps<any> };

type Props = SwitchExtProps & CommonProps;

const Switch: React.FC<Props> = ({ label, controller, value, error, dark = false, fullWidth, helperText, onChange, disabled }) => {
  const backgroundClass = dark ? 'Mui-backgroundDark' : 'Mui-backgroundLight';
  const errorClass = error ? 'Mui-error' : '';
  const disabledClass = disabled ? 'Mui-disabled' : '';

  const renderSwitch = (checked: boolean, onChangeFn: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void) => (
    <FormControlLabel
      sx={{
        marginLeft: 0,
        marginRight: 0,
        display: 'flex',
        gap: 2,
        minWidth: 0,
      }}
      classes={{
        label: `${backgroundClass} ${errorClass} ${disabledClass}`,
      }}
      control={
        <MuiSwitch
          checked={checked}
          disabled={disabled}
          onChange={onChangeFn}
          name={`${slugify(label)}-label`}
          slotProps={{
            root: {
              className: `${backgroundClass} ${errorClass} ${disabledClass}`,
            },
          }}
        />
      }
      label={label}
    />
  );

  return (
    <FormControl
      fullWidth={fullWidth}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        minWidth: 0,
      }}>
      {controller ? (
        <Controller
          {...controller}
          render={({ field, fieldState }) => (
            <>
              {renderSwitch(field.value, field.onChange)}
              {(helperText || fieldState.error?.message) && (
                <FormHelperText error={error ?? fieldState.invalid}>{helperText ?? fieldState.error?.message}</FormHelperText>
              )}
            </>
          )}
        />
      ) : (
        <Box display={'flex'} flexDirection={'column'}>
          {renderSwitch(value ?? false, (event, checked) => onChange?.(event, checked))}
          {helperText && (
            <FormHelperText className={backgroundClass} disabled={disabled} error={error}>
              {helperText}
            </FormHelperText>
          )}
        </Box>
      )}
    </FormControl>
  );
};

export default React.memo(Switch);
